import { runtimeConfig } from "../config/runtime";
import { timetableDB } from "../db/indexedDb";
import pako from "pako";

const getConfig = () => ({
  containerName: runtimeConfig.azureStorageContainerName || "jsonfiles",
  isProduction: runtimeConfig.isProduction,
  storageAccountUrl: runtimeConfig.storageUrl,
});

// Add deduplication cache for in-flight requests
const pendingRequests = new Map<string, Promise<any>>();
const DEDUPLICATION_TTL = 2000; // 2 seconds

type MissingBlobError = Error & {
  status: number;
  blobPath: string;
  endpointType: string;
  isMissingBlob: true;
  phase: "head" | "get";
};

const createMissingBlobError = (
  blobPath: string,
  endpointType: string,
  phase: "head" | "get",
): MissingBlobError => {
  const error = new Error(
    `Blob not found (404): ${blobPath} [${endpointType}] [${phase}]`,
  ) as MissingBlobError;
  error.status = 404;
  error.blobPath = blobPath;
  error.endpointType = endpointType;
  error.isMissingBlob = true;
  error.phase = phase;
  return error;
};

export const isMissingBlobNotFoundError = (
  error: unknown,
): error is MissingBlobError => {
  if (!error || typeof error !== "object") {
    return false;
  }

  const err = error as Partial<MissingBlobError>;
  return (
    err.isMissingBlob === true ||
    err.status === 404 ||
    (typeof err.message === "string" &&
      err.message.includes("Blob not found (404)"))
  );
};

// Helper to generate a consistent cache key for requests
const getCacheKey = (operation: string, params: any): string => {
  return `${operation}:${JSON.stringify(params)}`;
};

// Clear service worker cache for a specific URL
const clearServiceWorkerCache = async (url: string): Promise<void> => {
  try {
    if ("serviceWorker" in navigator && "caches" in window) {
      const cacheNames = await caches.keys();
      for (const cacheName of cacheNames) {
        const cache = await caches.open(cacheName);
        await cache.delete(url);
      }
    }
  } catch (error) {
    console.warn("Failed to clear service worker cache:", error);
  }
};

// Azure best practice: Add telemetry and logging
const logStorageOperation = (
  operation: string,
  details: any,
  isError = false,
) => {
  if (isError) {
    console.error(`[AZURE-STORAGE] ${operation} failed:`, details);
  } else if (getConfig().isProduction) {
    // In production, you would send this to Application Insights or another monitoring service
    console.debug(`[AZURE-STORAGE] ${operation} completed:`, details);
  }
};

// Get base URL for storage
export const getStorageBaseUrl = (): string => {
  const config = getConfig();

  // Fallback to direct Azure Storage
  return config.storageAccountUrl;
};

/**
 * Decompresses a gzipped or plain Uint8Array blob and returns a UTF-8 string.
 * @param data The Uint8Array of the blob (already fetched)
 * @param contentType Optional content-type header (used to check for gzip)
 * @returns The decompressed string
 */
const decompressBlob = (data: Uint8Array, contentType?: string): string => {
  // Detect gzip by content-type
  const isGzipped = contentType && contentType.includes("gzip");

  if (isGzipped) {
    try {
      // Use pako for decompression (make sure pako is imported)
      data = pako.ungzip(data);
    } catch (error) {
      console.error("Failed to decompress gzipped content:", error);
      return "";
    }
  }

  const decoder = new TextDecoder("utf-8");
  return decoder.decode(data);
};

export const azureStorageService = {
  /**
   * Get prayer times JSON from Azure Blob Storage by path using direct fetch
   */
  async getPrayerTimesFromBlob(
    masjidId: number | string,
    blobPath: string,
    forceRefresh: boolean = false,
  ): Promise<{ data: any; eTag?: string }> {
    // Generate a cache key for this specific request
    const cacheKey = getCacheKey("getPrayerTimesFromBlob", {
      masjidId,
      blobPath,
      forceRefresh,
    });

    // Check if there's already a pending request for this exact data
    if (!forceRefresh && pendingRequests.has(cacheKey)) {
      console.log(`Reusing in-flight request for ${blobPath}`);
      return pendingRequests.get(cacheKey) as Promise<{
        data: any;
        eTag?: string;
      }>;
    }

    // Create a new request promise and store it
    const requestPromise = (async () => {
      try {
        logStorageOperation("GetPrayerTimes", { masjidId, blobPath });

        // Parse the blob path to extract year and month
        // Expected format: timetables/{masjidId}/{year}/{month}.json.gz
        const pathMatch = blobPath.match(
          /timetables\/\d+\/(\d+)\/(\d+)\.json\.gz$/,
        );
        if (!pathMatch || pathMatch.length < 3) {
          throw new Error(`Invalid blob path format: ${blobPath}`);
        }

        const year = pathMatch[1];
        const month = pathMatch[2];

        // Offline guard: return cached data without making network calls
        if (!navigator.onLine) {
          console.log(
            `Device is offline, returning cached data for ${blobPath}`,
          );
          const cachedData = await timetableDB.getTimetable(
            year,
            month,
            masjidId.toString(),
          );
          if (cachedData) {
            return { data: cachedData, eTag: cachedData.eTag };
          }
          throw new Error(`Offline and no cached data for ${year}-${month}`);
        }

        // Construct the full URL (public access - no SAS token needed)
        const config = getConfig();
        const baseUrl = getStorageBaseUrl();
        const containerName = config.containerName;
        let url = `${baseUrl}/${containerName}/${blobPath}`;

        // Add cache-busting query param if forceRefresh is true
        if (forceRefresh) {
          url += `?t=${Date.now()}`;
        }

        const endpointType =
          baseUrl.includes("127.0.0.1") || baseUrl.includes("localhost")
            ? "Local Azurite"
            : "Direct Azure Storage";

        console.log(`Fetching blob via ${endpointType}:`, url.split("?")[0]);

        // Use standard fetch with appropriate cache settings
        const fetchOptions: RequestInit = {
          cache: "no-cache",
          headers: { "Cache-Control": "no-cache" },
          signal: AbortSignal.timeout(10000),
        };

        // Check cached eTag before fetching
        const baseBlobUrl = url.split("?")[0];
        const cachedETagRecord = await timetableDB.getETag(baseBlobUrl);
        const cachedETag = cachedETagRecord?.eTag;

        // Fetch eTag with HEAD request
        const headResponse = await fetch(url, {
          ...fetchOptions,
          method: "HEAD",
        });
        if (!headResponse.ok) {
          if (headResponse.status === 404) {
            console.warn("[AZURE-STORAGE] Timetable blob missing on HEAD", {
              blobPath,
              endpointType,
              status: headResponse.status,
            });
            throw createMissingBlobError(blobPath, endpointType, "head");
          }
          throw new Error(
            `Failed to fetch eTag: ${headResponse.status} ${headResponse.statusText}`,
          );
        }
        const currentETag = headResponse.headers.get("ETag") || undefined;

        // If eTag matches and not forcing refresh, return cached data or indicate no change
        if (!forceRefresh && cachedETag && currentETag === cachedETag) {
          console.log(`eTag matches for ${baseBlobUrl}, using cached data`);

          // Retrieve cached data from DB
          const cachedData = await timetableDB.getTimetable(
            year,
            month,
            masjidId.toString(),
          );
          if (cachedData) {
            return { data: cachedData, eTag: currentETag };
          }

          // If no cached data exists, fall through to fetch fresh data
          console.log(`No cached data found, fetching fresh data`);
        } else {
          // eTag changed or force refresh - clear service worker cache and add cache-busting
          if (currentETag !== cachedETag) {
            console.log(
              `🔄 [CACHE BUST] eTag changed from ${cachedETag} to ${currentETag}, clearing SW cache`,
            );
            // Clear service worker cache for this URL
            await clearServiceWorkerCache(baseBlobUrl);
            url += `${url.includes("?") ? "&" : "?"}cb=${Date.now()}`;
            console.log(
              `🔄 [CACHE BUST] eTag changed, adding cache-busting to URL`,
            );
          }
        }

        // Then do the GET fetch with the updated URL
        const response = await fetch(url, fetchOptions);
        if (!response.ok) {
          if (response.status === 404) {
            console.warn("[AZURE-STORAGE] Timetable blob missing on GET", {
              blobPath,
              endpointType,
              status: response.status,
            });
            throw createMissingBlobError(blobPath, endpointType, "get");
          }
          throw new Error(`Failed to fetch blob: ${response.statusText}`);
        }

        // Get ETag for change detection
        const eTag = response.headers.get("ETag") || undefined;
        const contentType = response.headers.get("Content-Type");

        // Check if the response is gzipped (either by file extension or content-type)
        const isGzipped =
          blobPath.endsWith(".json.gz") ||
          (contentType && contentType.includes("gzip"));

        let parsedData;
        if (isGzipped) {
          // Handle gzipped response
          const arrayBuffer = await response.arrayBuffer();
          const data = new Uint8Array(arrayBuffer);
          const jsonStr = decompressBlob(data, contentType || undefined);

          try {
            parsedData = JSON.parse(jsonStr);
          } catch (error: any) {
            throw new Error(
              `Failed to parse decompressed JSON: ${error.message}`,
            );
          }
        } else {
          // Handle regular JSON response
          parsedData = await response.json();
        }

        // Store the new eTag
        if (currentETag) {
          await timetableDB.saveETag(baseBlobUrl, currentETag);
        }

        return { data: parsedData, eTag: currentETag };
      } catch (error: any) {
        logStorageOperation(
          "GetPrayerTimes",
          {
            masjidId,
            blobPath,
            error: error.message,
            status: error?.status,
            isMissingBlob: error?.isMissingBlob === true,
            phase: error?.phase,
          },
          true,
        );

        throw error;
      } finally {
        // Remove from pending requests after a short delay
        // This prevents removing too early while still avoiding duplicate requests
        setTimeout(() => {
          pendingRequests.delete(cacheKey);
        }, DEDUPLICATION_TTL);
      }
    })();

    // Store the promise for potential reuse
    pendingRequests.set(cacheKey, requestPromise);

    return requestPromise;
  },
};

/**
 * Simplified getPrayerTimesFromBlob for public blob access
 */
export async function getPrayerTimesFromBlob(
  masjidId: string | number,
  blobPath: string,
): Promise<{ data: any; eTag: string | null }> {
  const url = `${getStorageBaseUrl()}/jsonfiles/${blobPath}`;
  console.log(`Fetching blob: ${url}`);

  try {
    const response = await fetch(url, {
      signal: AbortSignal.timeout(10000),
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch blob: ${response.status} ${response.statusText}`,
      );
    }

    const contentType = response.headers.get("Content-Type");
    const isGzipped =
      blobPath.endsWith(".json.gz") ||
      (contentType && contentType.includes("gzip"));

    let data;
    if (isGzipped) {
      const arrayBuffer = await response.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      const decompressed = pako.ungzip(uint8Array);
      const jsonString = new TextDecoder("utf-8").decode(decompressed);
      data = JSON.parse(jsonString);
    } else {
      data = await response.json();
    }

    const eTag = response.headers.get("etag");
    return { data, eTag };
  } catch (error) {
    console.error(`Blob fetch error for ${blobPath}:`, error);
    throw error;
  }
}
